import type { DraggableSyntheticListeners } from '@dnd-kit/core';
import cn from 'classnames';
import deepEqual from 'lodash/isEqual';
import { forwardRef, memo, useMemo } from 'react';

import { AnswerIcon } from '@webapp/common/components/misc';
import { SortableHandler, useSortable } from '@webapp/common/components/sortable';
import { useMobile } from '@webapp/common/hooks/use-mobile';
import { setHtml } from '@webapp/common/lib/ui';
import type { TestCheckStatus } from '@webapp/common/resources/survey';

import { usePipingAnswersReplacer } from '@webapp/survey/src/machine/use-piping-answers-replacer';

import css from './rank.css';

interface ItemProps {
    iconStyle: CSSProperties;
    textStyle: CSSProperties;
    itemStyle: CSSProperties;
    value: any;
    id: number;
    idx: number;
    className?: string;
    showAnswerIcon?: boolean;
    testCheckStatus?: TestCheckStatus;
    listeners?: DraggableSyntheticListeners;
}

export const Item = memo(
    forwardRef<HTMLDivElement, Omit<ItemProps, 'id'>>(
        (
            {
                className,
                iconStyle,
                idx,
                itemStyle,
                textStyle,
                value,
                showAnswerIcon,
                testCheckStatus,
                listeners,
                ...sortableProps
            },
            ref,
        ) => {
            const isMobile = useMobile();
            const pipingAnswersReplacer = usePipingAnswersReplacer();

            return (
                <div
                    className={cn(css.variant, className, isMobile && css.variantMobile)}
                    ref={ref}
                    style={itemStyle}
                    {...sortableProps}
                >
                    {showAnswerIcon && <AnswerIcon checked={true} testCheckStatus={testCheckStatus} />}
                    {isMobile ? (
                        <p className={css.order} style={textStyle}>
                            {idx}
                        </p>
                    ) : (
                        <SortableHandler
                            extraClassName={css.order}
                            extraStyle={textStyle}
                            listeners={listeners}
                            iconStyle={iconStyle}
                            withListeners
                        >
                            {idx}
                        </SortableHandler>
                    )}
                    <div
                        className={cn(css.text, isMobile && css.textMobile)}
                        style={textStyle}
                        {...setHtml(pipingAnswersReplacer(value[0]))}
                    />
                    {isMobile && (
                        <SortableHandler
                            big={true}
                            extraClassName={css.order}
                            extraStyle={textStyle}
                            listeners={listeners}
                            iconStyle={iconStyle}
                            withListeners
                        />
                    )}
                </div>
            );
        },
    ),
    deepEqual,
);
Item.displayName = 'Item';

const sortType = { type: 'rank' };

export const SortableItem: FC<Omit<ItemProps, 'listeners'>> = ({
    className,
    iconStyle,
    id,
    idx,
    itemStyle,
    showAnswerIcon,
    testCheckStatus,
    textStyle,
    value,
}) => {
    const { attributes, listeners, setNodeRef, style: sortableStyle } = useSortable(String(id), sortType);

    const style = useMemo(
        () => ({
            ...itemStyle,
            ...sortableStyle,
        }),
        [itemStyle, sortableStyle],
    );

    return (
        <Item
            ref={setNodeRef}
            {...attributes}
            listeners={listeners}
            className={className}
            iconStyle={iconStyle}
            idx={idx}
            itemStyle={style}
            textStyle={textStyle}
            showAnswerIcon={showAnswerIcon}
            testCheckStatus={testCheckStatus}
            value={value}
        />
    );
};
